import React from 'react';
import { Hash, AtSign, MessageSquare, AlertCircle, Image } from 'lucide-react';
import { TwitterContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import MediaUploader from '../components/MediaUploader';
import { PLATFORMS } from '../config/platformConfig';

interface TwitterEditorProps {
  content: TwitterContent;
  isEditing: boolean;
  onChange: (field: keyof TwitterContent, value: any) => void;
}

export default function TwitterEditor({ content, isEditing, onChange }: TwitterEditorProps) {
  const platform = PLATFORMS.twitter;
  const characterCount = (content.text || '').length;
  const isOverLimit = characterCount > 280;

  return (
    <div className="space-y-4">
      {/* Post Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Post Type</label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Tweet Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {content.postType === 'Thread' ? 'First Tweet' : 'Tweet Text'}
        </label>
        <div className="relative">
          <textarea
            value={content.text || ''}
            onChange={(e) => onChange('text', e.target.value)}
            disabled={!isEditing}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 h-24 ${
              isOverLimit
                ? 'border-red-500 focus:ring-red-500'
                : 'focus:ring-gray-800'
            }`}
            placeholder="What's happening?"
            maxLength={500} // Allow typing over limit for visibility
          />
          {isOverLimit && (
            <div className="absolute -top-6 right-0 flex items-center text-xs text-red-600">
              <AlertCircle className="w-3 h-3 mr-1" />
              Tweet is too long
            </div>
          )}
        </div>
        <div className="mt-2">
          <CharacterCounter
            current={characterCount}
            limit={280}
            label="Characters"
            showProgressBar={true}
          />
        </div>
      </div>

      {/* Thread (if Thread type selected) */}
      {content.postType === 'Thread' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MessageSquare className="inline w-4 h-4 mr-1" />
            Thread (Additional Tweets)
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Add each tweet on a new line (double line break between tweets)
          </p>
          <textarea
            value={content.threadTexts?.join('\n\n') || ''}
            onChange={(e) => {
              const texts = e.target.value.split('\n\n').filter(t => t.trim());
              onChange('threadTexts', texts);
            }}
            disabled={!isEditing}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 disabled:bg-gray-100 h-32"
            placeholder="Tweet 2...\n\nTweet 3...\n\nTweet 4..."
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Thread tweets: {content.threadTexts?.length || 0} / {platform.limits.thread}
            </p>
            {content.threadTexts && content.threadTexts.length > 0 && (
              <div className="text-xs space-x-2">
                {content.threadTexts.map((tweet, i) => {
                  const len = tweet.length;
                  return (
                    <span
                      key={i}
                      className={`${
                        len > 280 ? 'text-red-600' : 'text-gray-600'
                      }`}
                    >
                      Tweet {i + 2}: {len}/280
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Media Upload */}
      {(content.postType === 'Tweet' || content.postType === 'Thread' || content.postType === 'Image' || content.postType === 'Video') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Image className="inline w-4 h-4 mr-1" />
            Media Files
          </label>
          <MediaUploader
            media={content.media || []}
            onChange={(media) => onChange('media', media)}
            disabled={!isEditing}
            maxFiles={content.postType === 'Video' ? 1 : 4}
            acceptedTypes="image/*,video/*,image/gif"
            platformName="X"
          />
        </div>
      )}

      {/* Hashtags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Hash className="inline w-4 h-4 mr-1" />
          Hashtags
        </label>
        <input
          type="text"
          value={content.hashtags}
          onChange={(e) => onChange('hashtags', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 disabled:bg-gray-100"
          placeholder="#trending #news #tech"
        />
        <p className="text-xs text-gray-500 mt-1">
          Hashtags count toward your character limit
        </p>
      </div>

      {/* Mentions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <AtSign className="inline w-4 h-4 mr-1" />
          Mentions
        </label>
        <input
          type="text"
          value={content.mentions}
          onChange={(e) => onChange('mentions', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 disabled:bg-gray-100"
          placeholder="@user1 @user2"
        />
        <p className="text-xs text-gray-500 mt-1">
          Mentions count toward your character limit
        </p>
      </div>

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="X"
      />
    </div>
  );
}